import React, { memo, useCallback, useMemo, useRef } from "react";
import useTrack, { eventTypes } from "../../hooks/useTrack";

import useData from "../../hooks/useData";
import DUMMY_WIDGET_IMG from "../../img/dummy-widget.png";
import accessibilityProvider from "../../utils/accessibilityProvider";
import inPreview from "../../utils/inPreview";

import { FiChevronDown } from "react-icons/fi";
import useMode from "../../hooks/useMode";
import { Loader, Tag } from "../CoreUI";
import WidgetCardHead from "./WidgetCardHead";
import useWidget from "../../hooks/useWidget";
import useElementDimensions from "../../hooks/useElementDimensions";
import WidgetCardBody from "./WidgetCardBody";

import { genClassName } from "@disco/disco_core";

import "./widget-card.css";

export const WidgetCardLoadMore = memo(({ childAccessibilityProvider }) => {
	const { inPPE } = useMode();

	const { moreLoading, loadMoreBrands, lastLoadCount } = useData();
	const track = useTrack();

	const handleLoadMore = useCallback(() => {
		if (lastLoadCount === 0) return;
		loadMoreBrands();
		track(eventTypes.WIDGET_SEE_MORE_CLICK, {
			type: "LAZY_LOAD",
		});
	}, [loadMoreBrands, lastLoadCount, track]);

	if (moreLoading || (inPPE && !inPreview)) {
		return (
			<Loader
				className="widget-card-more-loader"
				{...(childAccessibilityProvider || {})}
			/>
		);
	}

	return (
		<Tag
			className="widget-card-load-cta"
			data-testid="widget-card-load-cta"
			onClick={handleLoadMore}
			{...accessibilityProvider({
				name: "Show more",
				role: "cell",
			})}
		>
			<span>
				<FiChevronDown />
			</span>{" "}
			Show more
		</Tag>
	);
});

// eslint-disable-next-line no-unused-vars
const WidgetCard = memo(({ setWidgetStyles }) => {
	const { l: brands, containerRef, moreError, feedHasUpsell } = useData();

	const rowCount = useMemo(
		() => (feedHasUpsell ? brands?.length + 1 : brands.length),
		[brands.length, feedHasUpsell]
	);
	const isEmpty = useMemo(() => rowCount === 0, [rowCount]);

	const showDummyImage = useMemo(() => inPreview && isEmpty, [isEmpty]);

	const widgetCardHeadRef = useRef(null);
	const { getHeight: getHeaderHeight } = useElementDimensions({
		elementRef: widgetCardHeadRef,
	});

	const { getWidth: getContainerWidth, getHeight: getContainerHeight } =
		useWidget({
			isEmpty,
			containerRef,
			canTrackLoad: true,
			intersectionId: "widget-card",
		});

	return (
		<>
			{showDummyImage && (
				<img
					src={DUMMY_WIDGET_IMG}
					className="widget-no-products-preview"
					alt="Disco"
				/>
			)}
			{!isEmpty && (
				<section
					className={genClassName({
						base: "widget-card",
					})}
					data-intersectionuid="widget-card"
					data-testid="widget-card"
					ref={containerRef}
				>
					<WidgetCardHead ref={widgetCardHeadRef} />
					<WidgetCardBody
						rowCount={rowCount}
						getContainerHeight={getContainerHeight}
						getContainerWidth={getContainerWidth}
						getHeaderHeight={getHeaderHeight}
					/>
					{moreError && (
						<section className="widget-card-more-error"></section>
					)}
				</section>
			)}
		</>
	);
});

export default WidgetCard;
